import { useCallback } from 'react';
import { Task, FullProject, LogEntry, ProjectMetadata } from '../../types';
import { ProjectSchema, TaskSchema } from '../../schemas/validationSchemas';
import { doc, deleteDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../firebase';
import { areProjectIdsMatching } from '../../utils/idNormalizer';

export const removeUndefined = <T>(obj: T): T => {
  if (Array.isArray(obj)) return (obj.map(v => removeUndefined(v)) as unknown) as T;
  if (obj !== null && typeof obj === 'object') {
    return (Object.fromEntries(
      Object.entries(obj)
        .filter(([_, v]) => v !== undefined)
        .map(([k, v]) => [k, removeUndefined(v)])
    ) as unknown) as T;
  }
  return obj;
};

export interface ProjectVersionBackup {
  timestamp: string | number;
  projectWithoutTasks: Omit<FullProject, 'tasks'>;
  tasks: Task[];
}

export interface UseProjectPersistenceProps {
  userId: string | null;
  tenantId: string;
  COMM_PROJECT_ID: string;
  INBOX_PROJECT_ID: string;
  currentProjectId: string;
  setTasks: React.Dispatch<React.SetStateAction<Task[]>>;
  setProjectName: (name: string) => void;
  setProjectMetadata: React.Dispatch<React.SetStateAction<ProjectMetadata>>;
  setChangelog: React.Dispatch<React.SetStateAction<LogEntry[]>>;
  setAllProjects: (updater: FullProject[] | ((prev: FullProject[]) => FullProject[])) => void;
  syncProjectToFirestore: (project: FullProject, isBackground?: boolean) => Promise<boolean | void | number | null>;
}

export function useProjectPersistence({
  userId,
  tenantId,
  COMM_PROJECT_ID,
  INBOX_PROJECT_ID,
  currentProjectId,
  setTasks,
  setProjectName,
  setProjectMetadata,
  setChangelog,
  setAllProjects,
  syncProjectToFirestore
}: UseProjectPersistenceProps) {

  const restoreProjectVersion = useCallback(async (projectId: string, versionData: ProjectVersionBackup) => {
    // 1. Validate versionData structure
    if (!versionData || !versionData.projectWithoutTasks || !versionData.tasks) {
      throw new Error('Ungültiges Backup-Format');
    }

    const restoredProjectMetadata = versionData.projectWithoutTasks;
    const restoredTasks = versionData.tasks;

    // 2. Validate using Zod schemas to align with guidelines
    try {
      ProjectSchema.parse(restoredProjectMetadata);
      restoredTasks.forEach((task: Task) => {
        const cleanTask = removeUndefined({
          ...task,
          projectId,
          userId: restoredProjectMetadata.userId || userId || 'unknown',
          tenantId: restoredProjectMetadata.tenantId || tenantId
        });
        TaskSchema.parse(cleanTask);
      });
    } catch (validationErr: unknown) {
      const errDetail = validationErr && typeof validationErr === 'object' && 'errors' in validationErr
        ? (validationErr as { errors: unknown }).errors
        : validationErr;
      console.error('[VersionControl] Zod validation failed for restoration:', errDetail);
      throw new Error('Validierung fehlgeschlagen: Backup-Daten entsprechen nicht dem aktuellen Schema');
    }

    // 3. Create restored log entry
    const entry: LogEntry = {
      id: Date.now().toString() + Math.random().toString(36).substring(2, 9),
      timestamp: new Date().toISOString(),
      action: 'reset',
      description: 'Projekt-Version wiederhergestellt',
      details: `Wiederhergestellt auf Stand von: ${new Date(versionData.timestamp).toLocaleString()}`
    };

    const updatedChangelog = [entry, ...(restoredProjectMetadata.changelog || [])].slice(0, 100);

    // 4. Construct full restored project
    const restoredProject: FullProject = {
      ...restoredProjectMetadata,
      tasks: restoredTasks,
      changelog: updatedChangelog,
      lastModified: Date.now()
    };

    // 5. Update local states if this is the active project
    if (areProjectIdsMatching(projectId, currentProjectId)) {
      setTasks(restoredTasks);
      setProjectName(restoredProjectMetadata.name || "");
      setProjectMetadata(restoredProjectMetadata.metadata || {});
      setChangelog(updatedChangelog);
    }

    // 6. Update allProjects state
    setAllProjects(prev => prev.map(p => areProjectIdsMatching(p.id, projectId) ? restoredProject : p));

    // 7. Force sync to Firestore
    await syncProjectToFirestore(restoredProject);
  }, [currentProjectId, userId, tenantId, syncProjectToFirestore, setAllProjects, setTasks, setProjectName, setProjectMetadata, setChangelog]);

  const deleteProjectFromPersistence = useCallback(async (id: string) => {
    const rawId = id === 'inbox_inforum' ? INBOX_PROJECT_ID : (id === 'comm_internal' ? COMM_PROJECT_ID : id);
    try {
      // 1. Cloud Delete Project
      await deleteDoc(doc(db, 'projects', rawId));
      
      // 2. Cloud Delete Tasks of that project
      const qTasks = query(collection(db, 'tasks'), where('projectId', '==', rawId));
      const tasksSnapshot = await getDocs(qTasks);
      const deletePromises = tasksSnapshot.docs.map(doc => deleteDoc(doc.ref));
      await Promise.all(deletePromises);

      // 3. Local Delete (SQLite Server)
      try {
        await fetch(`/api/projects/${rawId}`, { method: 'DELETE' });
      } catch (e) {
        console.warn('[Sync] Local project deletion failed (optional):', e);
      }
    } catch (err) {
      console.error('Failed to delete project in persistence:', err);
    }
  }, [COMM_PROJECT_ID, INBOX_PROJECT_ID]);

  return {
    restoreProjectVersion,
    deleteProjectFromPersistence
  };
}
